import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Bell, Package, Clock, TrendingUp, DollarSign, ArrowRight } from 'lucide-react';

const typeConfig = {
  low_stock: { color: 'border-l-red-400', icon: Package, route: '/dashboard/inventory' },
  delay_alert: { color: 'border-l-amber-400', icon: Clock, route: '/dashboard/orders' },
  upsell: { color: 'border-l-gold', icon: TrendingUp, route: '/dashboard/analytics' },
  spend_reminder: { color: 'border-l-blue-accent', icon: DollarSign, route: '/dashboard/analytics' },
};

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try { const { data } = await api.get('/notifications/unread-count'); setCount(data.count); } catch { /* */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try { const { data } = await api.get('/notifications'); setNotifications(data); } catch { /* */ }
  }, []);

  useEffect(() => { fetchCount(); const i = setInterval(fetchCount, 15000); return () => clearInterval(i); }, [fetchCount]);

  const toggleOpen = async () => {
    if (!open) {
      fetchNotifications();
      // Mark all as read when bell is clicked open
      try { await api.put('/notifications/mark-all-read'); } catch { /* */ }
      setCount(0);
    }
    setOpen(!open);
  };

  const handleClick = async (notif) => {
    try { await api.put(`/notifications/${notif.id}/read`); } catch { /* */ }
    setOpen(false);
    fetchCount();
    const config = typeConfig[notif.type];
    if (config?.route) {
      navigate(config.route);
    }
  };

  const markAllRead = async () => {
    try {
      await Promise.all(notifications.map(n => api.put(`/notifications/${n.id}/read`)));
      fetchCount();
      fetchNotifications();
    } catch { /* */ }
  };

  return (
    <div className="relative">
      <button onClick={toggleOpen} className="relative p-2 text-zinc-400 hover:text-white transition-colors" data-testid="notification-bell">
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid="notification-count">{count > 9 ? '9+' : count}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Dropdown — fixed to viewport so it never escapes the right edge on
              narrow phones (was using absolute right-0 which went off-screen
              when the bell sat on the right side of a 390px header). Width is
              clamped to the viewport with 8px gutters on each side. */}
          <div
            className="fixed z-50 max-h-[28rem] bg-bg-secondary border border-white/10 rounded-sm shadow-xl shadow-black/50 overflow-hidden"
            style={{
              top: '3.25rem',
              right: '0.5rem',
              left: 'auto',
              width: 'min(20rem, calc(100vw - 1rem))',
              maxWidth: 'calc(100vw - 1rem)'
            }}
            data-testid="notification-dropdown"
          >
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-sm uppercase tracking-[0.15em] text-zinc-400 font-sans font-bold">Notifications</span>
              <div className="flex items-center gap-2">
                {count > 0 && (
                  <button onClick={markAllRead} className="text-[11px] text-gold font-sans hover:underline" data-testid="mark-all-read">Mark all read</button>
                )}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-zinc-500 font-sans text-sm text-center py-6">No notifications</p>
              ) : notifications.map(n => {
                const config = typeConfig[n.type] || { color: 'border-l-zinc-500', icon: Bell, route: null };
                const Icon = config.icon;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`p-3 border-b border-white/5 border-l-2 ${config.color} hover:bg-white/[0.04] cursor-pointer transition-colors group`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <Icon className="w-3.5 h-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-sans text-sm font-medium leading-tight">{n.title}</p>
                        <p className="text-zinc-500 font-sans text-xs mt-0.5 leading-tight">{n.message}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-zinc-600 font-sans text-[11px]">{new Date(n.created_at).toLocaleTimeString()}</span>
                          {config.route && (
                            <span className="text-gold text-[11px] font-sans flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              View <ArrowRight className="w-2.5 h-2.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
